/**
 * EZX's fee, in the UI.
 *
 * MIRRORS the server's source of truth:
 *   ezx-server/src/features/pricing/platform-fee.helper.ts → computeFeeCents()
 *
 * A preview that disagrees with the server is worse than no preview, so the
 * formula is kept trivially small and pinned by tests using the same vectors as
 * the server's. If you change the fee math on the server, change it here and
 * update both test files, or the admin console will quote one number and the
 * checkout will charge another.
 *
 *     fee = round(carrierRate * percent/100) + flatCents
 *
 * Both parts always apply. Either may be zero.
 */

export interface PlatformFeeInput {
  /** 10 = 10% of the carrier's rate. */
  percent: number;
  /** 150 = $1.50 per shipment, whatever the parcel is worth. */
  flatCents: number;
}

export function computeFeeCents(
  carrierRateCents: number,
  fee: PlatformFeeInput
): number {
  return Math.round(carrierRateCents * (fee.percent / 100)) + fee.flatCents;
}

/**
 * The label prices the preview is shown against.
 *
 * One price tells you nothing. The regressive take rate — 20% of a $10 parcel
 * but 3% of a $60 one — is invisible at a single point and obvious across four.
 * That gap went unnoticed for months because no screen ever showed it.
 */
export const PREVIEW_RATES_CENTS = [500, 1000, 2500, 6000] as const;

export interface FeePreviewRow {
  carrierRateCents: number;
  feeCents: number;
  /** The fee as a share of the carrier's rate — "how much on top am I taking". */
  takePercent: number;
}

export function previewFee(fee: PlatformFeeInput): FeePreviewRow[] {
  return PREVIEW_RATES_CENTS.map((carrierRateCents) => {
    const feeCents = computeFeeCents(carrierRateCents, fee);
    return {
      carrierRateCents,
      feeCents,
      takePercent: (feeCents / carrierRateCents) * 100,
    };
  });
}

/**
 * True when the fee is flat-only, which makes the take rate REGRESSIVE: cheap
 * parcels are taxed hard and expensive ones — the ones carrying the most risk
 * and support cost — barely pay their way.
 */
export function isRegressive(fee: PlatformFeeInput): boolean {
  return fee.percent === 0 && fee.flatCents > 0;
}

export const formatCents = (cents: number): string =>
  `$${(cents / 100).toFixed(2)}`;
